import { c as createFocusTrap } from './focus-trap.esm-C-N_-G0z.js';

/**
 * `html.touch` (from `html-head-js.liquid`): mega menus use click only.
 * `html.no-touch`: hover (`mouseenter` / `mouseleave`).
 */
function useClickOnlyMegaNav() {
  return typeof window !== 'undefined' && document.documentElement.classList.contains('touch');
}

class HeaderNavigation extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */ #controllers = this.querySelectorAll(
    '[aria-controls^="header-navigation--"]'
  );
  /** @type {HTMLElement|null} */ #activeItem;
  /** @type {AbortController|null} */ #scrollController = null;
  /** @type {import('focus-trap').FocusTrap} */ #focusTrap;

  connectedCallback() {
    this.#controllers.forEach((controller) => {
      controller.addEventListener('mouseenter', () => this.#handleMouseenter(controller));
      controller.addEventListener('mouseleave', (event) =>
        this.#handleMouseleave(event, controller)
      );
      controller.addEventListener('keydown', (event) => this.#handleKeydown(event, controller));
      controller.addEventListener(
        'click',
        (event) => this.#handleControllerClick(event, controller),
        {
          capture: true,
        }
      );
    });
  }

  /**
   * Click-only mode: first tap opens (no navigation); second tap follows `href` when already open.
   */
  #handleControllerClick(event, controller) {
    if (!useClickOnlyMegaNav()) return;

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    if (controller.getAttribute('aria-expanded') !== 'true') {
      event.preventDefault();
      if (this.#activeItem && this.#activeItem.controller !== controller) {
        this.#close(this.#activeItem.controller, this.#activeItem.target);
      }
      this.#open(controller, target);
    }
  }

  /**
   * @param {HTMLElement} controller
   * @returns {HTMLElement|null}
   */
  #resolveAriaControlsTarget(controller) {
    const elements = controller.ariaControlsElements;
    if (elements?.length) return elements[0];
    const id = controller.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  #handleMouseenter(controller) {
    if (useClickOnlyMegaNav()) return;

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    this.#open(controller, target);
  }

  #handleMouseleave(event, controller) {
    if (useClickOnlyMegaNav()) return;

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    const nextElement = event.relatedTarget;

    if (
      nextElement instanceof Element &&
      (controller.contains(nextElement) || target.contains(nextElement))
    ) {
      return this.#watchTargetMouseleave(controller, target);
    }

    requestAnimationFrame(() => {
      if (controller.matches(':hover') || target.matches(':hover')) {
        this.#watchTargetMouseleave(controller, target);
        return;
      }

      this.#close(controller, target);
    });
  }

  #watchTargetMouseleave(controller, target) {
    target.addEventListener('mouseleave', () => this.#close(controller, target), {
      once: true,
    });
  }

  /**
   * Handle controller keydown
   * @param {KeyboardEvent} event
   * @param {HTMLElement} controller
   */
  #handleKeydown(event, controller) {
    if (event.code !== 'Space') return;

    event.preventDefault();

    const target = this.#resolveAriaControlsTarget(controller);
    if (!target) return;

    this.#activeItem ? this.#close(controller, target) : this.#open(controller, target);

    this.#focusTrap = createFocusTrap(target, {
      onDeactivate: () => this.#close(controller, target),
    });

    target.addEventListener('transitionend', () => this.#focusTrap.activate(), { once: true });
  }

  #open(controller, target) {
    this.#activeItem = { controller, target };

    controller.ariaExpanded = 'true';
    target.ariaHidden = 'false';

    target.querySelector('mega-menu-element')?.expandDefaultPanel?.();

    this.#addScrollListener(controller, target);
  }

  #close(controller, target) {
    this.#activeItem = null;

    controller.ariaExpanded = 'false';
    target.ariaHidden = 'true';

    target.querySelector('mega-menu-element')?.collapseAll?.();

    this.#scrollController?.abort();
    this.#scrollController = null;

    if (this.#focusTrap) this.#focusTrap = null;
  }

  #addScrollListener(controller, target) {
    this.#scrollController?.abort();

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', () => this.#close(controller, target), {
      once: true,
      signal: this.#scrollController.signal,
    });
  }
}

customElements.define('header-navigation', HeaderNavigation);
